import styled, { css } from 'styled-components'

// Container
export const DataTableContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`

// Header
export const DataTableHeader = styled.div`
  padding: 0px 0px 24px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0; /* Prevent header from shrinking */

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
    padding-left: 140px; /* Space for ViewToggle on left */
    min-height: 44px;
  }
`

export const DataTableActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  align-items: center;
`

// Column Settings
export const ColumnSettingsBtn = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.small};
    min-width: 36px;
    justify-content: center;

    /* Hide text on mobile, show only icon */
    span {
      display: none;
    }
  }
`

export const ColumnDropdown = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.default};
  z-index: 1000;
  min-width: 240px;
  padding: ${({ theme }) => theme.spacing.medium};
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translateY(${({ $active }) => ($active ? 0 : '-10px')});
  transition: all ${({ theme }) => theme.transitions.default};
`

export const ColumnDropdownHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const ColumnDropdownHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ColumnItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.xsmall} 0;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const ColumnCheckbox = styled.input`
  accent-color: ${({ theme }) => theme.colors.primary};
`

export const ColumnLabel = styled.span`
  flex: 1;
`

// Table
export const TableWrapper = styled.div<{ $stickyHeader?: boolean }>`
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 0; /* Important for flex child scrolling */
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-bottom: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium} 0 0;

  ${({ $stickyHeader }) =>
    $stickyHeader &&
    css`
      thead {
        position: sticky;
        top: 0;
        z-index: 10;
        background: ${({ theme }) => theme.colors.background.secondary};
        height: 48px; /* Adjust based on your header height */
      }
    `}
`

export const Table = styled.table<{
  $bordered?: boolean
  $hover?: boolean
  $compact?: boolean
}>`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  ${({ $bordered, theme }) =>
    $bordered &&
    css`
      border: 1px solid ${theme.colors.border.light};

      th,
      td {
        border: 1px solid ${theme.colors.border.light};
      }
    `}

  ${({ $hover }) =>
    $hover &&
    css`
      tbody tr:hover {
        background: ${({ theme }) => theme.colors.background.secondary};
      }
    `}
    
  ${({ $compact }) =>
    $compact &&
    css`
      th,
      td {
        padding: ${({ theme }) => theme.spacing.small}
          ${({ theme }) => theme.spacing.medium};
      }
    `}
`

export const TableHead = styled.thead`
  background: ${({ theme }) => theme.colors.background.secondary};
`

export const TableHeader = styled.th<{ $hideOnMobile?: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  user-select: none;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.sortable {
    cursor: pointer;

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
    }

    &.sort-asc::after,
    &.sort-desc::after {
      content: '';
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
    }

    &.sort-asc::after {
      border-bottom: 6px solid ${({ theme }) => theme.colors.primary};
    }

    &.sort-desc::after {
      border-top: 6px solid ${({ theme }) => theme.colors.primary};
    }
  }

  @media (max-width: 768px) {
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }
`

export const TableBody = styled.tbody``

export const TableRow = styled.tr<{ $selected?: boolean }>`
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  transition: background ${({ theme }) => theme.transitions.default};
  cursor: pointer;

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.colors.primaryLight};
    `}

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`

export const TableCell = styled.td<{ $hideOnMobile?: boolean }>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;

  @media (max-width: 768px) {
    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      css`
        display: none;
      `}
  }
`

// Checkbox cell
export const CheckboxCell = styled(TableCell)`
  width: 48px;
  padding: 0;
  text-align: center;
`

export const CheckboxCellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
`

// Pagination
export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  flex-shrink: 0; /* Prevent pagination from shrinking */
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.medium}
    ${({ theme }) => theme.borderRadius.medium};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.small};
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
    justify-content: center;
  }
`

export const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};

  strong {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    order: 3;
    flex-basis: 100%;
    text-align: center;
    padding-top: ${({ theme }) => theme.spacing.xsmall};
    border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: 768px) {
    order: 1;
    gap: ${({ theme }) => theme.spacing.xsmall};
  }
`

export const PaginationBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 28px;
    height: 28px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`

export const PaginationPages = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const PaginationPage = styled.button<{ $active: boolean }>`
  min-width: 32px;
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing.small};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.background.primary};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover:not(:disabled) {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    min-width: 28px;
    height: 28px;
    padding: 0 ${({ theme }) => theme.spacing.xsmall};
    font-size: 12px;
  }
`

export const PaginationPerPage = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: 768px) {
    order: 2;
    gap: ${({ theme }) => theme.spacing.xsmall};
  }
`

export const PerPageLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

export const PerPageSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 12px;
    padding: ${({ theme }) => theme.spacing.micro}
      ${({ theme }) => theme.spacing.xsmall};
  }
`

// Bulk Actions
export const BulkActionsBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.large};
  z-index: 1000;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translate(-50%, 100%);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    bottom: 16px;
    left: 16px;
    right: 16px;
    transform: none;
    padding: ${({ theme }) => theme.spacing.small}
      ${({ theme }) => theme.spacing.medium};
    gap: ${({ theme }) => theme.spacing.medium};
    flex-wrap: wrap;
    justify-content: center;

    @keyframes slideUp {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
`

export const BulkSelectionInfo = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: 12px;
    flex-basis: 100%;
    text-align: center;
  }
`

export const BulkActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.xsmall};
  }
`

export const BulkActionBtn = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger'
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.xsmall}
      ${({ theme }) => theme.spacing.small};
    font-size: 12px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  ${({ $variant = 'secondary', theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};
          border: 1px solid ${theme.colors.primary};

          &:hover {
            background: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
          }
        `
      case 'danger':
        return css`
          background: ${theme.colors.background.primary};
          color: ${theme.colors.danger};
          border: 1px solid ${theme.colors.danger};

          &:hover {
            background: ${theme.colors.danger};
            color: ${theme.colors.text.inverse};
          }
        `
      default:
        return css`
          background: ${theme.colors.background.primary};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.default};

          &:hover {
            background: ${theme.colors.background.secondary};
            border-color: ${theme.colors.text.secondary};
          }
        `
    }
  }}
`

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xlarge};
  text-align: center;
`

export const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  margin-bottom: ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.tertiary};

  svg {
    width: 100%;
    height: 100%;
  }
`

export const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.small};
`

export const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 400px;
`

// Resize and Drag handles
export const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 12px;
  cursor: col-resize;
  user-select: none;
  z-index: 2;
  transform: translateX(50%);

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: transparent;
    transform: translateX(-50%);
    transition: all 0.2s;
  }

  &:hover::before {
    background: ${({ theme }) => theme.colors.primary};
    width: 3px;
  }

  &:active::before {
    background: ${({ theme }) => theme.colors.primary};
    width: 4px;
  }
`

export const DragHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 24px;
  cursor: grab;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.default};

  th:hover & {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: '⋮⋮';
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const DragPreview = styled.div`
  position: fixed;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: 12px;
  font-weight: 600;
  pointer-events: none;
  z-index: 9999;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`

// Detail Panel
export const DetailPanel = styled.div<{
  $isOpen: boolean
  $width?: string | number
}>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '600px'};
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    width: 70%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

export const DetailPanelHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const DetailPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.large};
`

export const DetailPanelClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`
